/**
 * Health Service Interface
 * Cross-platform health data integration for iOS (HealthKit) and Android (Health Connect)
 */

import {
  HealthSummary,
  HealthPermissions,
  WorkoutData,
  SleepData,
  HeartRateData,
  WeightData,
  BloodPressureData,
  HealthServiceConfig,
  ApiResponse
} from '../types';

export interface HealthService {
  /**
   * Initialize the health service
   */
  initialize(): Promise<ApiResponse<void>>;

  /**
   * Request health permissions
   */
  requestPermissions(permissions: Partial<HealthPermissions>): Promise<ApiResponse<HealthPermissions>>;

  /**
   * Check current permissions status
   */
  checkPermissions(): Promise<ApiResponse<HealthPermissions>>;

  /**
   * Get health summary for a specific date
   */
  getHealthSummary(date: Date): Promise<ApiResponse<HealthSummary>>;

  /**
   * Get health summary for a date range
   */
  getHealthSummaryRange(startDate: Date, endDate: Date): Promise<ApiResponse<HealthSummary[]>>;

  /**
   * Get steps for a specific date
   */
  getSteps(date: Date): Promise<ApiResponse<number>>;

  /**
   * Get calories burned for a specific date
   */
  getCalories(date: Date): Promise<ApiResponse<number>>;

  /**
   * Get distance walked/run for a specific date
   */
  getDistance(date: Date): Promise<ApiResponse<number>>;

  /**
   * Get active minutes for a specific date
   */
  getActiveMinutes(date: Date): Promise<ApiResponse<number>>;

  /**
   * Get workouts for a specific date
   */
  getWorkouts(date: Date): Promise<ApiResponse<WorkoutData[]>>;

  /**
   * Get sleep data for a specific date
   */
  getSleep(date: Date): Promise<ApiResponse<SleepData | null>>;

  /**
   * Get heart rate data for a specific date
   */
  getHeartRate(date: Date): Promise<ApiResponse<HeartRateData[]>>;

  /**
   * Get weight data for a specific date
   */
  getWeight(date: Date): Promise<ApiResponse<WeightData | null>>;

  /**
   * Get blood pressure data for a specific date
   */
  getBloodPressure(date: Date): Promise<ApiResponse<BloodPressureData | null>>;

  /**
   * Save a workout to the health platform
   */
  saveWorkout(workout: Omit<WorkoutData, 'id' | 'source'>): Promise<ApiResponse<string>>;

  /**
   * Save sleep data to the health platform
   */
  saveSleep(sleep: Omit<SleepData, 'id' | 'source'>): Promise<ApiResponse<string>>;

  /**
   * Save weight data to the health platform
   */
  saveWeight(weight: Omit<WeightData, 'id' | 'source'>): Promise<ApiResponse<string>>;

  /**
   * Save blood pressure data to the health platform
   */
  saveBloodPressure(bp: Omit<BloodPressureData, 'id' | 'source'>): Promise<ApiResponse<string>>;

  /**
   * Start observing health data changes
   */
  startObserving(callback: (data: HealthSummary) => void): Promise<ApiResponse<void>>;

  /**
   * Stop observing health data changes
   */
  stopObserving(): Promise<ApiResponse<void>>;

  /**
   * Check if health service is available on this platform
   */
  isAvailable(): boolean;

  /**
   * Test HealthKit availability and basic functionality
   */
  testHealthKitAvailability(): Promise<{ available: boolean; error?: string }>;

  /**
   * Get the health service configuration
   */
  getConfig(): HealthServiceConfig;

  /**
   * Update the health service configuration
   */
  updateConfig(config: Partial<HealthServiceConfig>): Promise<ApiResponse<void>>;
}

/**
 * Health Service Factory
 * Creates platform-specific health service implementations
 */
export class HealthServiceFactory {
  static create(): HealthService {
    // Platform-specific implementation will be injected
    throw new Error('HealthServiceFactory.create() must be implemented by platform-specific code');
  }
} 